/**
 * QR encoding (byte mode, error correction level M), for the codes on printed booking documents.
 *
 * <p>
 * Written rather than installed: the encoders on npm arrive with canvas shims, DOM adapters and
 * render layers this project does not want, and what is actually needed here is a bit stream, a
 * Reed–Solomon remainder and a module grid. `qrcode` pulls in a PNG writer to draw something this
 * project renders as SVG.
 * </p>
 *
 * <p>
 * This replaced a Code 128 encoder, which is in the history if a linear symbol is ever wanted
 * again. QR won on three counts: it survives a fold and a thermal fade through its error
 * correction, it carries a URL a customer's phone can act on, and it can hold a shop name in
 * Gujarati, which subset B could not encode at all.
 * </p>
 *
 * <p>
 * <strong>Level M and byte mode only, deliberately.</strong> M recovers 15% of the symbol, which is
 * the usual default and the right one here: a booking slip lives in a pocket for six days and a
 * thermal print fades, so L is too fragile, while Q and H would grow the symbol enough to matter on
 * a 72mm roll. Byte mode encodes anything; alphanumeric mode would pack an uppercase-and-digits
 * booking number more tightly, but a URL has lowercase and slashes in it and would fall back to
 * byte anyway — one path that always works beats a smaller symbol on half the payloads.
 * </p>
 *
 * <p>
 * Versions 1 to 10 are supported, which is 213 bytes at level M — far more than a booking number, a
 * unit serial, or the status URL that will replace them. Beyond that the symbol is too dense to
 * print at 28mm and the honest answer is a shorter payload, not a bigger code.
 * </p>
 */

/* ------------------------------------------------------------ GF(256) */

/**
 * Log and antilog tables over GF(256) with primitive polynomial 0x11D, which is the field QR's
 * Reed–Solomon is defined in. Built once at module load — 512 bytes to avoid a multiply loop per
 * codeword.
 */
const exp = new Uint8Array(512);
const log = new Uint8Array(256);

{
  let x = 1;

  for (let i = 0; i < 255; i += 1) {
    exp[i] = x;
    log[x] = i;
    x <<= 1;

    if (x & 0x100) {
      x ^= 0x11d;
    }
  }

  // Doubling the exponent table lets `exp[a + b]` skip a modulo on every multiply.
  for (let i = 255; i < 512; i += 1) {
    exp[i] = exp[i - 255]!;
  }
}

function mul(a: number, b: number): number {
  return a === 0 || b === 0 ? 0 : exp[log[a]! + log[b]!]!;
}

/**
 * The generator polynomial for `degree` error-correction codewords — `(x−α⁰)(x−α¹)…(x−α^(n−1))`.
 *
 * <p>
 * <strong>Coefficients are in descending order: `poly[0]` is the leading term.</strong> That is
 * stated because it is the thing to get wrong. An ascending-order generator paired with a
 * descending-order division produces codewords that look entirely plausible — right length, right
 * range, different for different inputs — and that no scanner on earth will decode. The syndrome
 * test beside this is what catches it.
 * </p>
 */
function generator(degree: number): number[] {
  let poly = [1];

  for (let i = 0; i < degree; i += 1) {
    const next = new Array<number>(poly.length + 1).fill(0);

    for (let j = 0; j < poly.length; j += 1) {
      // Multiply the running product by (x + α^i): once shifted up a power, once scaled.
      next[j] = next[j]! ^ poly[j]!;
      next[j + 1] = next[j + 1]! ^ mul(poly[j]!, exp[i]!);
    }

    poly = next;
  }

  return poly;
}

/**
 * The Reed–Solomon remainder for one block — the error-correction codewords appended to it.
 *
 * <p>
 * Polynomial long division in GF(256): the message shifted up by `degree` places, reduced by the
 * generator. It is the only part of a QR encoder that is arithmetic rather than bookkeeping, and
 * the only part whose failure is completely invisible in the rendered symbol.
 * </p>
 */
export function errorCorrection(data: readonly number[], degree: number): number[] {
  const gen = generator(degree);
  const remainder = [...data, ...new Array<number>(degree).fill(0)];

  for (let i = 0; i < data.length; i += 1) {
    const factor = remainder[i]!;

    if (factor === 0) {
      continue;
    }

    for (let j = 0; j < gen.length; j += 1) {
      remainder[i + j] = remainder[i + j]! ^ mul(gen[j]!, factor);
    }
  }

  return remainder.slice(data.length);
}

/* ------------------------------------------------------- version table */

interface BlockGroup {
  blocks: number;
  dataCodewords: number;
}

interface VersionSpec {
  /** Error-correction codewords per block. */
  ecPerBlock: number;
  groups: BlockGroup[];
}

/**
 * Level M only. Each entry is the block layout the standard fixes for that version — QR does not
 * derive these, it tabulates them, and so does everything that implements it.
 */
const versions: Record<number, VersionSpec> = {
  1: { ecPerBlock: 10, groups: [{ blocks: 1, dataCodewords: 16 }] },
  2: { ecPerBlock: 16, groups: [{ blocks: 1, dataCodewords: 28 }] },
  3: { ecPerBlock: 26, groups: [{ blocks: 1, dataCodewords: 44 }] },
  4: { ecPerBlock: 18, groups: [{ blocks: 2, dataCodewords: 32 }] },
  5: { ecPerBlock: 24, groups: [{ blocks: 2, dataCodewords: 43 }] },
  6: { ecPerBlock: 16, groups: [{ blocks: 4, dataCodewords: 27 }] },
  7: { ecPerBlock: 18, groups: [{ blocks: 4, dataCodewords: 31 }] },
  8: {
    ecPerBlock: 22,
    groups: [
      { blocks: 2, dataCodewords: 38 },
      { blocks: 2, dataCodewords: 39 },
    ],
  },
  9: {
    ecPerBlock: 22,
    groups: [
      { blocks: 3, dataCodewords: 36 },
      { blocks: 2, dataCodewords: 37 },
    ],
  },
  10: {
    ecPerBlock: 26,
    groups: [
      { blocks: 4, dataCodewords: 43 },
      { blocks: 1, dataCodewords: 44 },
    ],
  },
};

/** Centres of the alignment patterns, per version. */
const alignmentCentres: Record<number, number[]> = {
  1: [],
  2: [6, 18],
  3: [6, 22],
  4: [6, 26],
  5: [6, 30],
  6: [6, 34],
  7: [6, 22, 38],
  8: [6, 24, 42],
  9: [6, 26, 46],
  10: [6, 28, 50],
};

const maxVersion = 10;

function dataCapacity(version: number): number {
  const spec = versions[version]!;

  return spec.groups.reduce((sum, group) => sum + group.blocks * group.dataCodewords, 0);
}

/** Size in modules. Every QR is `4 × version + 17` on a side. */
export function moduleCount(version: number): number {
  return version * 4 + 17;
}

/* ------------------------------------------------------- bit assembly */

class BitBuffer {
  private readonly bits: number[] = [];

  put(value: number, length: number): void {
    for (let i = length - 1; i >= 0; i -= 1) {
      this.bits.push((value >>> i) & 1);
    }
  }

  get length(): number {
    return this.bits.length;
  }

  /** Pads to a whole number of codewords and returns them. */
  toCodewords(capacity: number): number[] {
    const bits = [...this.bits];

    // Terminator: up to four zero bits, then zeros to the next byte boundary.
    const terminator = Math.min(4, capacity * 8 - bits.length);

    for (let i = 0; i < terminator; i += 1) {
      bits.push(0);
    }

    while (bits.length % 8 !== 0) {
      bits.push(0);
    }

    const codewords: number[] = [];

    for (let i = 0; i < bits.length; i += 8) {
      let byte = 0;

      for (let j = 0; j < 8; j += 1) {
        byte = (byte << 1) | bits[i + j]!;
      }

      codewords.push(byte);
    }

    // The two pad codewords the standard names, alternating until the block is full.
    const pad = [0xec, 0x11];

    while (codewords.length < capacity) {
      codewords.push(pad[(codewords.length - bits.length / 8) % 2]!);
    }

    return codewords;
  }
}

/** UTF-8 bytes, because a shop name or an address can carry anything. */
function toBytes(value: string): number[] {
  return [...new TextEncoder().encode(value)];
}

/**
 * The smallest version that holds `byteLength` at level M, or null if none does.
 *
 * The character-count indicator is 8 bits below version 10 and 16 bits from version 10, which is
 * why the comparison adds the header rather than comparing raw lengths.
 */
function chooseVersion(byteLength: number): number | null {
  for (let version = 1; version <= maxVersion; version += 1) {
    const countBits = version < 10 ? 8 : 16;
    const needed = Math.ceil((4 + countBits + byteLength * 8) / 8);

    if (needed <= dataCapacity(version)) {
      return version;
    }
  }

  return null;
}

/**
 * Interleaves the blocks, which is what makes a burst of damage recoverable.
 *
 * Data codewords are taken one per block in turn, then the error-correction codewords the same way.
 * A scratch across the symbol then damages one codeword of each block rather than destroying one
 * block entirely — which is the whole point of splitting into blocks in the first place.
 */
function interleave(codewords: number[], version: number): number[] {
  const spec = versions[version]!;

  const blocks: { data: number[]; ec: number[] }[] = [];
  let offset = 0;

  for (const group of spec.groups) {
    for (let i = 0; i < group.blocks; i += 1) {
      const data = codewords.slice(offset, offset + group.dataCodewords);
      offset += group.dataCodewords;
      blocks.push({ data, ec: errorCorrection(data, spec.ecPerBlock) });
    }
  }

  const result: number[] = [];
  const longest = Math.max(...blocks.map((block) => block.data.length));

  for (let i = 0; i < longest; i += 1) {
    for (const block of blocks) {
      if (i < block.data.length) {
        result.push(block.data[i]!);
      }
    }
  }

  for (let i = 0; i < spec.ecPerBlock; i += 1) {
    for (const block of blocks) {
      result.push(block.ec[i]!);
    }
  }

  return result;
}

/* --------------------------------------------------------- the matrix */

type Grid = (0 | 1 | null)[][];

function blank(size: number): Grid {
  return Array.from({ length: size }, () => new Array<0 | 1 | null>(size).fill(null));
}

function placeFinder(grid: Grid, row: number, col: number): void {
  const size = grid.length;

  for (let dr = -1; dr <= 7; dr += 1) {
    for (let dc = -1; dc <= 7; dc += 1) {
      const r = row + dr;
      const c = col + dc;

      if (r < 0 || r >= size || c < 0 || c >= size) {
        continue;
      }

      const onRing = dr === 0 || dr === 6 || dc === 0 || dc === 6;
      const inCore = dr >= 2 && dr <= 4 && dc >= 2 && dc <= 4;
      const inside = dr >= 0 && dr <= 6 && dc >= 0 && dc <= 6;

      grid[r]![c] = inside && (onRing || inCore) ? 1 : 0;
    }
  }
}

function placeFunctionPatterns(grid: Grid, version: number): void {
  const size = grid.length;

  placeFinder(grid, 0, 0);
  placeFinder(grid, 0, size - 7);
  placeFinder(grid, size - 7, 0);

  for (let i = 8; i < size - 8; i += 1) {
    const bit: 0 | 1 = i % 2 === 0 ? 1 : 0;
    grid[6]![i] = bit;
    grid[i]![6] = bit;
  }

  const centres = alignmentCentres[version]!;

  for (const r of centres) {
    for (const c of centres) {
      // The three finder corners have no alignment pattern over them.
      if ((r === 6 && c === 6) || (r === 6 && c === size - 7) || (r === size - 7 && c === 6)) {
        continue;
      }

      for (let dr = -2; dr <= 2; dr += 1) {
        for (let dc = -2; dc <= 2; dc += 1) {
          grid[r + dr]![c + dc] = Math.max(Math.abs(dr), Math.abs(dc)) === 1 ? 0 : 1;
        }
      }
    }
  }

  // The one module that is always dark.
  grid[size - 8]![8] = 1;
}

/** Reserves the format and version areas so the data pass skips them. */
function reserve(grid: Grid, version: number): boolean[][] {
  const size = grid.length;
  const taken = grid.map((row) => row.map((cell) => cell !== null));

  for (let i = 0; i < 9; i += 1) {
    taken[8]![i] = true;
    taken[i]![8] = true;
  }

  for (let i = 0; i < 8; i += 1) {
    taken[8]![size - 1 - i] = true;
    taken[size - 1 - i]![8] = true;
  }

  if (version >= 7) {
    for (let i = 0; i < 6; i += 1) {
      for (let j = 0; j < 3; j += 1) {
        taken[size - 11 + j]![i] = true;
        taken[i]![size - 11 + j] = true;
      }
    }
  }

  return taken;
}

/**
 * Walks the data codewords into the grid, two columns at a time, upwards then downwards.
 *
 * Column 6 is skipped entirely — it is the vertical timing pattern, and the standard's zigzag steps
 * over it rather than around it.
 */
function placeData(grid: Grid, taken: boolean[][], codewords: number[]): void {
  const size = grid.length;
  let bit = 0;
  let upward = true;

  for (let right = size - 1; right > 0; right -= 2) {
    const col = right <= 6 ? right - 1 : right;

    for (let step = 0; step < size; step += 1) {
      const row = upward ? size - 1 - step : step;

      for (const c of [col, col - 1]) {
        if (taken[row]![c]) {
          continue;
        }

        const byte = codewords[bit >>> 3] ?? 0;
        grid[row]![c] = ((byte >>> (7 - (bit & 7))) & 1) as 0 | 1;
        bit += 1;
      }
    }

    upward = !upward;
  }
}

const maskRules: ((r: number, c: number) => boolean)[] = [
  (r, c) => (r + c) % 2 === 0,
  (r) => r % 2 === 0,
  (_, c) => c % 3 === 0,
  (r, c) => (r + c) % 3 === 0,
  (r, c) => (Math.floor(r / 2) + Math.floor(c / 3)) % 2 === 0,
  (r, c) => ((r * c) % 2) + ((r * c) % 3) === 0,
  (r, c) => (((r * c) % 2) + ((r * c) % 3)) % 2 === 0,
  (r, c) => (((r + c) % 2) + ((r * c) % 3)) % 2 === 0,
];

/**
 * The standard's four penalty rules, summed.
 *
 * Lower is better. This is what stops a symbol coming out with a large blank field or a pattern that
 * mimics a finder — both of which a scanner mistakes for something else.
 */
function penalty(grid: Grid): number {
  const size = grid.length;
  let score = 0;

  const lines: (0 | 1)[][] = [];

  for (let i = 0; i < size; i += 1) {
    lines.push(grid[i]!.map((cell) => cell ?? 0));
    lines.push(grid.map((row) => row[i] ?? 0));
  }

  // Rule 1 — runs of five or more.
  for (const line of lines) {
    let run = 1;

    for (let i = 1; i < line.length; i += 1) {
      if (line[i] === line[i - 1]) {
        run += 1;
      } else {
        if (run >= 5) {
          score += run - 2;
        }

        run = 1;
      }
    }

    if (run >= 5) {
      score += run - 2;
    }
  }

  // Rule 2 — 2x2 blocks of one colour.
  for (let r = 0; r < size - 1; r += 1) {
    for (let c = 0; c < size - 1; c += 1) {
      const a = grid[r]![c];

      if (a === grid[r]![c + 1] && a === grid[r + 1]![c] && a === grid[r + 1]![c + 1]) {
        score += 3;
      }
    }
  }

  // Rule 3 — the finder-like 1:1:3:1:1 sequence with four light modules beside it.
  const patterns = [
    [1, 0, 1, 1, 1, 0, 1, 0, 0, 0, 0],
    [0, 0, 0, 0, 1, 0, 1, 1, 1, 0, 1],
  ];

  for (const line of lines) {
    for (let i = 0; i + 11 <= line.length; i += 1) {
      for (const pattern of patterns) {
        if (pattern.every((bit, j) => line[i + j] === bit)) {
          score += 40;
        }
      }
    }
  }

  // Rule 4 — deviation from an even split of dark and light.
  const dark = grid.flat().filter((cell) => cell === 1).length;
  const ratio = (dark * 100) / (size * size);
  score += Math.floor(Math.abs(ratio - 50) / 5) * 10;

  return score;
}

/** Format information: two EC bits and three mask bits, BCH-protected and XOR-masked. */
function formatBits(mask: number): number {
  // Level M is 0b00.
  const data = mask;
  let bch = data << 10;

  for (let i = 4; i >= 0; i -= 1) {
    if (bch & (1 << (i + 10))) {
      bch ^= 0x537 << i;
    }
  }

  return (((data << 10) | bch) ^ 0x5412) & 0x7fff;
}

function placeFormat(grid: Grid, mask: number): void {
  const size = grid.length;
  const bits = formatBits(mask);

  for (let i = 0; i < 15; i += 1) {
    const bit = ((bits >>> i) & 1) as 0 | 1;

    // The copy around the top-left finder.
    if (i < 6) {
      grid[8]![i] = bit;
    } else if (i === 6) {
      grid[8]![7] = bit;
    } else if (i === 7) {
      grid[8]![8] = bit;
    } else if (i === 8) {
      grid[7]![8] = bit;
    } else {
      grid[14 - i]![8] = bit;
    }

    // The redundant copy split between the other two corners.
    if (i < 8) {
      grid[8]![size - 1 - i] = bit;
    } else {
      grid[size - 15 + i]![8] = bit;
    }
  }
}

function placeVersion(grid: Grid, version: number): void {
  if (version < 7) {
    return;
  }

  const size = grid.length;
  let bch = version << 12;

  for (let i = 5; i >= 0; i -= 1) {
    if (bch & (1 << (i + 12))) {
      bch ^= 0x1f25 << i;
    }
  }

  const bits = (version << 12) | bch;

  for (let i = 0; i < 18; i += 1) {
    const bit = ((bits >>> i) & 1) as 0 | 1;
    const r = Math.floor(i / 3);
    const c = i % 3;

    grid[size - 11 + c]![r] = bit;
    grid[r]![size - 11 + c] = bit;
  }
}

/* ------------------------------------------------------------- public */

export interface QrSymbol {
  /** `true` is a dark module. Row-major, `size × size`. */
  modules: boolean[][];
  size: number;
  version: number;
}

/** Whether `value` fits a version-10 symbol at level M, which is 213 UTF-8 bytes. */
export function fits(value: string): boolean {
  return value.length > 0 && chooseVersion(toBytes(value).length) !== null;
}

/**
 * Encodes `value` into a QR symbol.
 *
 * @throws {RangeError} on an empty value or one too long for version 10. Guard with {@link fits}.
 */
export function encodeQr(value: string): QrSymbol {
  const bytes = toBytes(value);
  const version = value.length > 0 ? chooseVersion(bytes.length) : null;

  if (version === null) {
    throw new RangeError('QR payload is empty or larger than a version-10 level-M symbol holds');
  }

  const buffer = new BitBuffer();
  buffer.put(0b0100, 4); // byte mode
  buffer.put(bytes.length, version < 10 ? 8 : 16);

  for (const byte of bytes) {
    buffer.put(byte, 8);
  }

  const codewords = interleave(buffer.toCodewords(dataCapacity(version)), version);

  const size = moduleCount(version);
  const base = blank(size);

  placeFunctionPatterns(base, version);
  const taken = reserve(base, version);
  placeData(base, taken, codewords);
  placeVersion(base, version);

  // Every mask is tried and the least-penalised wins, which is what the standard asks for and what
  // stops a payload happening to produce a symbol a scanner reads as a finder pattern.
  let best: Grid | null = null;
  let bestScore = Number.POSITIVE_INFINITY;

  for (let mask = 0; mask < 8; mask += 1) {
    const candidate = base.map((row) => [...row]);

    for (let r = 0; r < size; r += 1) {
      for (let c = 0; c < size; c += 1) {
        if (!taken[r]![c] && maskRules[mask]!(r, c)) {
          candidate[r]![c] = candidate[r]![c] === 1 ? 0 : 1;
        }
      }
    }

    placeFormat(candidate, mask);

    const score = penalty(candidate);

    if (score < bestScore) {
      bestScore = score;
      best = candidate;
    }
  }

  return {
    modules: best!.map((row) => row.map((cell) => cell === 1)),
    size,
    version,
  };
}
